import { Input, message, Modal, Select, DatePicker, TimePicker } from "antd";
import moment from "moment";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import { get } from "lodash";

// ─── Styled Components ────────────────────────────────────────────────────────

const ModalBody = styled.div`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }
  .form-row-single {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
  .form-row-4 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    @media (max-width: 768px) { grid-template-columns: 1fr 1fr; }
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    label {
      font-size: 13px;
      font-weight: 500;
      color: #333;
      .required { color: red; margin-left: 2px; }
    }
  }
  textarea {
    border: 1px solid #d9d9d9;
    border-radius: 3px;
    padding: 10px;
    font-size: 14px;
    resize: vertical;
    min-height: 90px;
    outline: none;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;
    &:focus { border-color: #00c48c; }
  }
  .section-title {
    font-size: 15px;
    font-weight: 600;
    margin: 8px 0 12px;
    color: #222;
  }
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    cursor: pointer;
    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #00c48c;
      border: none;
      padding: 0;
    }
  }
  .reminder-block {
    margin-bottom: 16px;
  }
  .reminder-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    .reminder-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        accent-color: #00c48c;
        cursor: pointer;
      }
      label {
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;
        white-space: nowrap;
      }
    }
    .reminder-inputs {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      .ant-input { flex: 1; }
      .ant-select { flex: 1; }
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  .btn-cancel {
    padding: 8px 24px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
  }
  .btn-create {
    padding: 8px 24px;
    border: none;
    border-radius: 6px;
    background: #c6f4e7;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
`;

const ErrorMsg = styled.span`
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 2px;
`;

// ─── Helper ───────────────────────────────────────────────────────────────────

function parseDateTimeString(str) {
  if (!str) return { date: null, time: null };
  const m = moment(str, ["YYYY-MM-DD HH:mm", moment.ISO_8601]);
  if (!m.isValid()) return { date: null, time: null };
  return { date: m.clone().startOf("day"), time: m.clone() };
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateEventModal = ({ visible, onClose, editData, isEditMode: isEditModeProp }) => {
  const isEditMode = isEditModeProp || !!editData;

  const [loading, setLoading] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [matters, setMatters] = useState([]);
  const [loadingMatters, setLoadingMatters] = useState(false);

  const userList = useSelector(getUserList) || [];
  const userInfo = useSelector((store) => get(store, "common.userInfo"));

  // ── Form state ──
  const [eventName, setEventName] = useState("");
  const [matter, setMatter] = useState(null);
  const [startDateTime, setStartDateTime] = useState({ date: null, time: null });
  const [endDateTime, setEndDateTime] = useState({ date: null, time: null });
  const [location, setLocation] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [responsiblePersons, setResponsiblePersons] = useState([]);
  const [description, setDescription] = useState("");
  const [sendReminder, setSendReminder] = useState(false);
  const [reminderCount, setReminderCount] = useState(1);
  const [reminderType, setReminderType] = useState("minutes");
  const [hideFromAll, setHideFromAll] = useState(false);
  const [errors, setErrors] = useState({});
  const [reminderError, setReminderError] = useState("");

  const seededRef = useRef(false);

  // ── Reset / seed on modal open/close ──
  useEffect(() => {
    if (!visible) {
      seededRef.current = false;
      setErrors({});
      if (!isEditMode) {
        setEventName("");
        setMatter(null);
        setStartDateTime({ date: null, time: null });
        setEndDateTime({ date: null, time: null });
        setLocation("");
        setMeetingLink("");
        setEmployees([]);
        setClients([]);
        setResponsiblePersons([]);
        setDescription("");
        setSendReminder(false);
        setReminderCount(1);
        setReminderType("minutes");
        setHideFromAll(false);
        setReminderError("");
      }
      return;
    }

    if (isEditMode && editData && !seededRef.current) {
      seededRef.current = true;
      setEventName(editData.eventname || "");
      setMatter(editData.matterid ? String(editData.matterid) : null);
      setStartDateTime(parseDateTimeString(editData.startdatetime));
      setEndDateTime(parseDateTimeString(editData.enddatetime));
      setLocation(editData.location || "");
      setMeetingLink(editData.meetinglink || "");
      setEmployees(Array.isArray(editData.attendees) ? editData.attendees : []);
      setClients(Array.isArray(editData.clients) ? editData.clients : []);
      setResponsiblePersons(
        Array.isArray(editData.responsiblepersons) ? editData.responsiblepersons : []
      );
      setDescription(editData.description || "");
      setHideFromAll(!!editData.calendarstatus);
      const count = editData.sendremaindercount || editData.sendremindercount || "";
      const type = editData.sendremaindertype || editData.sendremindertype || "minutes";
      if (count) {
        setSendReminder(true);
        setReminderCount(Number(count));
        setReminderType(type);
      } else {
        setSendReminder(false);
        setReminderCount(1);
        setReminderType("minutes");
      }
    }
  }, [visible, isEditMode, editData]);

  // ── Resolve edit-mode matter id → object once matters are loaded ──
  useEffect(() => {
    if (isEditMode && matter && typeof matter === "string" && matters.length > 0) {
      const found = matters.find((m) => String(m.value) === matter);
      if (found) setMatter(found);
    }
  }, [matters, isEditMode]);

  // ── Fetch reference data ──
  const fetchMatters = useCallback(async () => {
    try {
      setLoadingMatters(true);
      const response = await CALL_FEATCH_API(
        `insyts-matter/getallmatterlist?userId=${userInfo?.userid}`,
        "get"
      );
      if (response?.status === "SUCCESS" && response?.response?.status) {
        const formatted = (response?.response?.data || []).map((item, index) => {
          const name = item?.matter_name || item?.mattername || item?.name || "";
          const id = item?.matterid || "";
          return {
            key: id || index,
            value: String(id),
            label: `${name} (${id})`,
            clientid: item?.clientid || "",
          };
        });
        setMatters(formatted);
      }
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    } finally {
      setLoadingMatters(false);
    }
  }, [userInfo?.userid]);

  const fetchClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      const response = await CALL_API("/insyts-legal/getAllClientList", "post");
      if (response?.code === "SUCCESS") {
        const formatted = (response.data || []).map((item) => ({
          value: item?.bdo_id,
          label:
            item?.clienttype === "person"
              ? item?.clientname
              : item?.companyregisteredname,
          responsiblepersonnel: item?.responsiblepersonnel || [],
          clienttype: item?.clienttype,
          // Store extra fields needed for payload
          email: item?.email || item?.emailid || "",
          clientname: item?.clientname || item?.companyregisteredname || "",
        }));
        setClientsList(formatted);
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchMatters();
      fetchClients();
    }
  }, [visible, fetchMatters, fetchClients]);

  // ── Derived options ──
  const userOptions = userList
    ?.filter((u) => u?.statusid === "1")
    ?.map((u) => ({ value: u?.userid, label: u?.fullname || u?.firstname }));

  const matterOptions = clients.length > 0
    ? matters.filter((m) => clients.includes(m.clientid))
    : matters;

  const filteredClients = matter?.clientid
    ? clientsList.filter((c) => c.value === matter.clientid)
    : clientsList;

  const selectedClientObjs = clientsList.filter((c) => clients.includes(c.value));
  const hasCompanyClient = selectedClientObjs.some((c) => c.clienttype === "company");
  const showResponsiblePersons = clients.length > 0 && hasCompanyClient;

  const responsibleOptions = selectedClientObjs
    .flatMap((c) =>
      (c.responsiblepersonnel || []).map((p) => ({ value: p.email, label: p.name }))
    );

  // ── Date/time disabling helpers ──
  const disabledEndDate = (current) => {
    if (!startDateTime?.date) return false;
    return current && current.isBefore(startDateTime.date, "day");
  };

  const disabledEndTime = () => {
    if (!startDateTime?.date || !endDateTime?.date || !startDateTime?.time) return {};
    const sameDay = moment(startDateTime.date).isSame(endDateTime.date, "day");
    if (!sameDay) return {};

    const startMoment = moment(startDateTime.time);
    const startHour24 = startMoment.hour();
    const startMin = startMoment.minute();
    const startHour12 = startHour24 % 12;
    const startIsPM = startHour24 >= 12;

    return {
      disabledHours: () => {
        const disabled = [];
        for (let h = 0; h <= 11; h++) {
          if (!startIsPM && h < startHour12) disabled.push(h);
          if (!startIsPM && h === startHour12) disabled.push(h);
        }
        return disabled;
      },
      disabledMinutes: (selectedHour) => {
        if (selectedHour === startHour12) {
          return Array.from({ length: startMin + 1 }, (_, i) => i);
        }
        return [];
      },
    };
  };

  // ── Build payload ──
  const buildPayload = (mode = "create") => {
    const start = moment(startDateTime?.date).set({
      hour: moment(startDateTime?.time).hour(),
      minute: moment(startDateTime?.time).minute(),
    });
    const end = moment(endDateTime?.date).set({
      hour: moment(endDateTime?.time).hour(),
      minute: moment(endDateTime?.time).minute(),
    });

    const attendeesname = employees.map((id) => {
      const user = userList?.find((u) => u?.userid === id);
      return user?.fullname || user?.firstname || id;
    });

    const firstSelectedClient = selectedClientObjs[0] || null;
    const clientpersonemailid = firstSelectedClient?.email || "";
    const clientpersonname = firstSelectedClient?.clientname || firstSelectedClient?.label || "";
    const clienttype = firstSelectedClient?.clienttype || "";

    // ✅ Collect all selected client names as an array
    const clientnames = selectedClientObjs.map(
      (c) => c.clientname || c.label || ""
    );

    const responsiblepersonelname = showResponsiblePersons
      ? responsiblePersons.map((email) => {
        const match = responsibleOptions.find((r) => r.value === email);
        return match?.label || email;
      })
      : [];

    // ✅ Get current user's full name
    const currentUser = userList?.find((u) => u?.userid === userInfo?.userid);
    const currentUserName = currentUser?.fullname || currentUser?.firstname || "";

    const base = {
      eventname: eventName,
      matterid: matter?.value ? String(matter.value) : "",
      mattername: matter?.label || editData?.mattername || "",
      startdatetime: start.format("YYYY-MM-DD HH:mm"),
      enddatetime: end.format("YYYY-MM-DD HH:mm"),
      location,
      meetinglink: meetingLink,
      attendees: employees,
      attendeesname,
      clients,
      clientnames,          // ✅ array of client names
      clientpersonemailid,
      clientpersonname,
      clienttype,
      responsiblepersons: showResponsiblePersons ? responsiblePersons : [],
      responsiblepersonelname,
      description,
      sendremaindercount: sendReminder ? String(reminderCount) : "",
      sendremaindertype: sendReminder ? reminderType : "",
      allcalendarstatus: hideFromAll,
      sendremainderstatus: sendReminder,
    };

    if (mode === "create") {
      base.createdby = userInfo?.userid || "";
      base.createdbyname = currentUserName;       // ✅ creator name
    } else if (mode === "update") {
      base.updatedby = userInfo?.userid || "";
      base.updatedbyname = currentUserName;       // ✅ updater name
    }

    return base;
  };

  // ── Validation ──
  const validate = () => {
    const newErrors = {};

    if (!eventName.trim()) newErrors.eventName = "Event name is required";
    if (!startDateTime?.date) newErrors.startDate = "Start date is required";
    if (!startDateTime?.time) newErrors.startTime = "Start time is required";
    if (!endDateTime?.date) newErrors.endDate = "End date is required";
    if (!endDateTime?.time) newErrors.endTime = "End time is required";

    if (
      startDateTime?.date && startDateTime?.time &&
      endDateTime?.date && endDateTime?.time
    ) {
      const start = moment(startDateTime.date).set({
        hour: moment(startDateTime.time).hour(),
        minute: moment(startDateTime.time).minute(),
      });
      const end = moment(endDateTime.date).set({
        hour: moment(endDateTime.time).hour(),
        minute: moment(endDateTime.time).minute(),
      });
      if (!end.isAfter(start)) {
        newErrors.endDate = "End date & time must be after start date & time";
        newErrors.endTime = " ";
      }
    }

    if (showResponsiblePersons && responsiblePersons.length === 0) {
      newErrors.responsiblePersons = "Responsible person is required for company clients";
    }

    if (sendReminder) {
      if (!reminderCount || Number(reminderCount) <= 0) {
        newErrors.reminder = "Value must be greater than zero";
      } else if (reminderError) {
        newErrors.reminder = reminderError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit handlers ──
  const handleCreate = async () => {
    if (!validate()) return;
    const payload = buildPayload("create");   // ✅ passes createdby + createdbyname
    try {
      setLoading(true);
      const res = await CALL_FEATCH_API("insyts-calendar/addevents", "post", payload);
      if (res?.status === 200 || res?.status === "SUCCESS") {
        message.success("Event created successfully");
        onClose(true);
      } else {
        message.error(res?.message || "Failed to create event");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!validate()) return;
    const payload = {
      ...buildPayload("update"),              // ✅ passes updatedby + updatedbyname
      eventid: editData?.eventid,
    };
    try {
      setLoading(true);
      const res = await CALL_FEATCH_API("insyts-calendar/editevents", "patch", payload);
      if (res?.status === 200 || res?.status === "SUCCESS") {
        message.success("Event updated successfully");
        onClose(true);
      } else {
        message.error(res?.message || "Failed to update event");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = isEditMode ? handleEdit : handleCreate;

  // ── Render ──
  return (
    <Modal
      title={isEditMode ? "Edit Event" : "New Event"}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <ModalBody>
        {/* Row 1: Event Name + Matter */}
        <div className="form-row">
          <div className="form-group">
            <label>Event Name <span className="required">*</span></label>
            <Input value={eventName} onChange={(e) => setEventName(e.target.value)} />
            {errors.eventName && <ErrorMsg>{errors.eventName}</ErrorMsg>}
          </div>

          <div className="form-group">
            <label>Matter</label>
            <Select
              showSearch
              placeholder="Select matter"
              loading={loadingMatters}
              options={matterOptions}
              value={matter?.value ?? undefined}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(val) => {
                if (val) {
                  const linked = matters.find((m) => String(m.value) === String(val));
                  if (linked) {
                    setMatter(linked);
                    if (linked.clientid && clients.length > 0 && !clients.includes(linked.clientid)) {
                      setClients([]);
                      setResponsiblePersons([]);
                    }
                    setResponsiblePersons([]);
                  }
                } else {
                  setMatter(null);
                  setResponsiblePersons([]);
                }
                setErrors((p) => ({ ...p, responsiblePersons: "" }));
              }}
              allowClear
              notFoundContent="No data"
            />
          </div>
        </div>

        {/* Row 2: Start Date | Start Time | End Date | End Time */}
        <div className="form-row-4">
          <div className="form-group">
            <label>Start Date <span className="required">*</span></label>
            <DatePicker
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              value={startDateTime?.date}
              status={errors.startDate ? "error" : ""}
              onChange={(date) => {
                setStartDateTime((prev) => ({ ...prev, date }));
                setErrors((p) => ({ ...p, startDate: "", endDate: "", endTime: "" }));
                if (endDateTime?.date && date && endDateTime.date.isBefore(date, "day")) {
                  setEndDateTime({ date: null, time: null });
                }
              }}
            />
            {errors.startDate && <ErrorMsg>{errors.startDate}</ErrorMsg>}
          </div>

          <div className="form-group">
            <label>Start Time <span className="required">*</span></label>
            <TimePicker
              style={{ width: "100%" }}
              value={startDateTime?.time}
              format="hh:mm A"
              status={errors.startTime ? "error" : ""}
              onChange={(time) => {
                setStartDateTime((prev) => ({ ...prev, time }));
                setErrors((p) => ({ ...p, startTime: "", endDate: "", endTime: "" }));

                if (endDateTime?.date && startDateTime?.date && endDateTime?.time && time) {
                  const sameDay = moment(startDateTime.date).isSame(endDateTime.date, "day");
                  if (sameDay) {
                    const newStart = moment(startDateTime.date).set({
                      hour: time.hour(),
                      minute: time.minute(),
                    });
                    const existingEnd = moment(endDateTime.date).set({
                      hour: moment(endDateTime.time).hour(),
                      minute: moment(endDateTime.time).minute(),
                    });
                    if (!existingEnd.isAfter(newStart)) {
                      setErrors((p) => ({
                        ...p,
                        endTime: "End time must be after start time",
                      }));
                    }
                  }
                }
              }}
            />
            {errors.startTime && <ErrorMsg>{errors.startTime}</ErrorMsg>}
          </div>

          <div className="form-group">
            <label>End Date <span className="required">*</span></label>
            <DatePicker
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              value={endDateTime?.date}
              status={errors.endDate ? "error" : ""}
              disabledDate={disabledEndDate}
              onChange={(date) => {
                setEndDateTime((prev) => ({ ...prev, date }));
                setErrors((p) => ({ ...p, endDate: "", endTime: "" }));
              }}
            />
            {errors.endDate && errors.endDate.trim() && (
              <ErrorMsg>{errors.endDate}</ErrorMsg>
            )}
          </div>

          <div className="form-group">
            <label>End Time <span className="required">*</span></label>
            <TimePicker
              style={{ width: "100%" }}
              value={endDateTime?.time}
              format="hh:mm A"
              status={errors.endTime ? "error" : ""}
              onChange={(time) => {
                setEndDateTime((prev) => ({ ...prev, time }));

                if (time && startDateTime?.date && startDateTime?.time && endDateTime?.date) {
                  const startFull = moment(startDateTime.date).set({
                    hour: moment(startDateTime.time).hour(),
                    minute: moment(startDateTime.time).minute(),
                  });
                  const endFull = moment(endDateTime.date).set({
                    hour: time.hour(),
                    minute: time.minute(),
                  });

                  if (!endFull.isAfter(startFull)) {
                    const sameDay = moment(startDateTime.date).isSame(endDateTime.date, "day");
                    if (sameDay) {
                      setErrors((p) => ({
                        ...p,
                        endTime: "End time must be after start time",
                        endDate: "",
                      }));
                    } else {
                      setErrors((p) => ({
                        ...p,
                        endDate: "End date must be after start date",
                        endTime: "",
                      }));
                    }
                  } else {
                    setErrors((p) => ({ ...p, endTime: "", endDate: "" }));
                  }
                } else {
                  setErrors((p) => ({ ...p, endTime: "", endDate: "" }));
                }
              }}
            />
            {errors.endTime && errors.endTime.trim() && (
              <ErrorMsg>{errors.endTime}</ErrorMsg>
            )}
          </div>
        </div>

        {/* Row 3: Location + Meeting Link */}
        <div className="form-row">
          <div className="form-group">
            <label>Location</label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Meeting Link</label>
            <Input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} />
          </div>
        </div>

        <div style={{ borderTop: "1px solid #eee", margin: "16px 0" }} />
        <div className="section-title">Choose Attendees</div>

        {/* Employees */}
        <div className="form-group" style={{ marginBottom: "16px" }}>
          <label>Choose employees</label>
          <Select
            mode="multiple"
            options={userOptions}
            value={employees}
            onChange={setEmployees}
            placeholder="Select employees"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Choose Client</label>
            <Select
              mode="multiple"
              options={filteredClients}
              loading={loadingClients}
              value={clients}
              onChange={(val) => {
                setClients(val);

                if (matter?.clientid && val.length > 0 && !val.includes(matter.clientid)) {
                  setMatter(null);
                }

                const stillHasCompany = clientsList
                  .filter((c) => val.includes(c.value))
                  .some((c) => c.clienttype === "company");
                if (!stillHasCompany) setResponsiblePersons([]);
                setErrors((p) => ({ ...p, responsiblePersons: "" }));
              }}
              placeholder={"Select clients"}
              notFoundContent="No data"
              allowClear
            />
          </div>

          {showResponsiblePersons && (
            <div className="form-group">
              <label>
                Responsible Personnel <span className="required">*</span>
              </label>
              <Select
                mode="multiple"
                options={responsibleOptions}
                value={responsiblePersons}
                onChange={(val) => {
                  setResponsiblePersons(val);
                  setErrors((p) => ({ ...p, responsiblePersons: "" }));
                }}
                placeholder="Select responsible persons"
                notFoundContent="No data"
              />
              {errors.responsiblePersons && (
                <ErrorMsg>{errors.responsiblePersons}</ErrorMsg>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: "8px" }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={hideFromAll}
              onChange={(e) => setHideFromAll(e.target.checked)}
            />
            Check this box to hide this event from All calendar Tab
          </label>
        </div>

        <div style={{ borderTop: "1px solid #eee", margin: "16px 0" }} />

        {/* Description */}
        <div className="form-group" style={{ marginBottom: "20px" }}>
          <label>Description</label>
          <Input.TextArea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Reminder */}
        <div className="reminder-block">
          <div className="reminder-row">
            <div className="reminder-checkbox">
              <input
                type="checkbox"
                id="send-reminder"
                checked={sendReminder}
                onChange={(e) => {
                  setSendReminder(e.target.checked);
                  if (!e.target.checked) setReminderError("");
                }}
              />
              <label htmlFor="send-reminder">Send Reminder before</label>
            </div>

            {sendReminder && (
              <div className="reminder-inputs" style={{ alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Input
                    type="number"
                    min={1}
                    value={reminderCount}
                    status={reminderError || errors.reminder ? "error" : ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const val = Number(raw);

                      if (val <= 0) {
                        setReminderError("Value must be greater than zero");
                        setReminderCount(raw);
                        return;
                      }

                      if (reminderType === "minutes" && val > 60) {
                        setReminderError("Minutes cannot exceed 60");
                      } else if (reminderType === "hours" && val > 24) {
                        setReminderError("Hours cannot exceed 24");
                      } else if (reminderType === "days" && val > 30) {
                        setReminderError("Days cannot exceed 30");
                      } else if (reminderType === "weeks" && val > 52) {
                        setReminderError("Weeks cannot exceed 52");
                      } else {
                        setReminderError("");
                      }
                      setReminderCount(val);
                    }}
                  />
                  {(reminderError || errors.reminder) && (
                    <ErrorMsg style={{ marginTop: "4px" }}>
                      {reminderError || errors.reminder}
                    </ErrorMsg>
                  )}
                </div>

                <Select
                  value={reminderType}
                  onChange={(val) => {
                    setReminderType(val);
                    setReminderError("");
                    const v = Number(reminderCount);
                    if (v <= 0) {
                      setReminderError("Value must be greater than zero");
                    }
                  }}
                  options={[
                    { value: "minutes", label: "Minutes" },
                    { value: "hours", label: "Hours" },
                    { value: "days", label: "Days" },
                    { value: "weeks", label: "Weeks" },
                  ]}
                />
              </div>
            )}
          </div>
        </div>

        <ModalFooter>
          <button style={{ border: "1px solid #000000", color: '#000000' }} className="btn-cancel" onClick={onClose}>Cancel</button>
          <button style={{ background: '#00685f', color: 'white' }} className="btn-create" onClick={handleSubmit} disabled={loading}>
            {loading
              ? isEditMode ? "Saving..." : "Creating..."
              : isEditMode ? "Save Changes" : "Create"}
          </button>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
};

export default CreateEventModal;